import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ArchiveIcon,
  ChevronDown,
  ChevronRight,
  GroupPeople,
  Pencil,
  Plus,
} from 'assets/icons';
import ApiDatabase from 'server';
import UseContext from 'hooks/useContext';
import Button from 'components/button';
import UseModal from 'components/modal/useModal';
import {
  getPerimeterErrorMessage,
  isCanceledRequest,
  isStalePerimeterError,
} from 'components/perimeter/perimeterErrors';
import {
  formatMemberName,
  sortMembersByName,
} from 'components/perimeter/perimeterMembers';
import {
  NO_PERIMETER_TARGET,
  PERIMETER_MODALS,
  getArchiveModalType,
} from 'components/perimeter/perimeterUsage';
import {
  fillText,
  formatShortDate,
  getCountText,
  perimeterText,
} from 'components/perimeter/perimeterText';

const ARCHIVED_LIST_ID = 'company-perimeters-archived-list';

const memberNames = (perimeter) =>
  sortMembersByName(perimeter.members || [])
    .map(formatMemberName)
    .filter(Boolean)
    .join(', ');

// The « Périmètres » block of the contacts tab: the admin manages the
// company's perimeters as its Administrator would from the company app.
const SlideOverCompanyPerimeters = ({ companyInfo, perimeters }) => {
  const { toggle } = UseModal();
  const { language, setModalData } = UseContext();
  const t = (key) => perimeterText(language, key);
  const idCompany = companyInfo?._id;
  const { status, active, archived, assignableMembers, hasArchivedError } =
    perimeters;
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isArchivedOpen, setIsArchivedOpen] = useState(false);
  const [checkingUsageId, setCheckingUsageId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setIsCollapsed(false);
    setIsArchivedOpen(false);
  }, [idCompany]);

  const openModal = (type, data) => {
    setModalData({ idCompany, ...data });
    toggle(type);
  };

  const handleCreate = () =>
    openModal(PERIMETER_MODALS.create, { assignableMembers });

  const handleEdit = (perimeter) =>
    openModal(PERIMETER_MODALS.edit, { perimeter, assignableMembers });

  // A request that failed on a perimeter which changed meanwhile: the toast
  // says so and the lists are read again.
  const reportFailure = (err) => {
    console.error(err);
    toast.error(getPerimeterErrorMessage(language, err));
    if (isStalePerimeterError(err)) perimeters.reload();
  };

  // The usage tells which modal fits (delete, archive or archive with a
  // transfer), so it is read before any modal opens.
  const handleArchive = (perimeter) => {
    if (checkingUsageId) return;
    setCheckingUsageId(perimeter.id);
    ApiDatabase.getAdminCompanyPerimeterUsage(
      { idCompany, id: perimeter.id, transferTo: NO_PERIMETER_TARGET },
      (res) => {
        if (!isMountedRef.current) return;
        setCheckingUsageId(null);
        const usage = res?.data;
        if (!usage) {
          toast.error(t('errors.generic'));
          return;
        }
        openModal(getArchiveModalType(usage), {
          perimeter,
          usage,
          perimeters: active,
        });
      },
      (err) => {
        if (!isMountedRef.current || isCanceledRequest(err)) return;
        setCheckingUsageId(null);
        reportFailure(err);
      },
    );
  };

  const handleRestore = (perimeter) => {
    if (restoringId) return;
    // The finished missions come back restricted to a perimeter that has no
    // member yet, so the admin confirms before restoring.
    if (Number(perimeter.usage?.finishedMissions) > 0) {
      openModal(PERIMETER_MODALS.restore, {
        perimeter,
        usage: perimeter.usage,
      });
      return;
    }
    setRestoringId(perimeter.id);
    ApiDatabase.postRestoreAdminCompanyPerimeter(
      { idCompany, id: perimeter.id },
      () => {
        if (!isMountedRef.current) return;
        setRestoringId(null);
        toast.success(fillText(t('toast.restored'), { name: perimeter.name }));
        perimeters.reload();
      },
      (err) => {
        if (!isMountedRef.current) return;
        setRestoringId(null);
        reportFailure(err);
      },
    );
  };

  const archivedDetails = (perimeter) => {
    const archivedOn = formatShortDate(perimeter.archivedAt);
    return [
      archivedOn
        ? fillText(t('archived.archivedOn'), { date: archivedOn })
        : '',
      getCountText(
        language,
        'archived.finishedMissions',
        Number(perimeter.usage?.finishedMissions) || 0,
      ),
    ]
      .filter(Boolean)
      .join(' · ');
  };

  const canCreate = status === 'ready';

  const renderActive = () =>
    active.length === 0 ? (
      <div
        data-testid={'perimeters-empty'}
        className={
          'flex flex-col items-center gap-2 rounded-xl border border-dashed border-gray-200 px-6 py-8 text-center'
        }
      >
        <div
          aria-hidden={'true'}
          className={
            'mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100'
          }
        >
          <GroupPeople wh={18} color={'#374151'} />
        </div>
        <div className={'font-medium text-gray-900'}>{t('empty.title')}</div>
        <p className={'max-w-md text-sm text-gray-500'}>
          {t('empty.description')}
        </p>
      </div>
    ) : (
      <ul>
        {active.map((perimeter) => {
          const names = memberNames(perimeter) || t('list.noMembers');
          return (
            <li
              key={perimeter.id}
              className={
                'py-2 border-b flex justify-between gap-2 items-center'
              }
            >
              <div className={'min-w-0'}>
                <div className={'truncate'} title={perimeter.name}>
                  {perimeter.name}
                </div>
                <div className={'truncate text-gray-500'} title={names}>
                  {names}
                </div>
              </div>
              <div className={'flex items-center shrink-0'}>
                <Button
                  size={'ICON'}
                  color={'ICON'}
                  onClick={() => handleEdit(perimeter)}
                >
                  <Pencil wh={20} color={'#111827'} />
                  <span className={'sr-only'}>
                    {`${t('list.edit')} ${perimeter.name}`}
                  </span>
                </Button>
                <Button
                  size={'ICON'}
                  color={'ICON'}
                  disabled={checkingUsageId !== null}
                  onClick={() => handleArchive(perimeter)}
                >
                  <ArchiveIcon wh={20} color={'#111827'} />
                  <span className={'sr-only'}>
                    {`${t('list.archive')} ${perimeter.name}`}
                  </span>
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    );

  const renderArchived = () => {
    if (hasArchivedError) {
      return (
        <div className={'flex flex-wrap items-center gap-3 text-gray-500'}>
          {t('archived.loadError')}
          <Button color={'SECONDARY'} size={'SM'} onClick={perimeters.reload}>
            {t('list.retry')}
          </Button>
        </div>
      );
    }
    if (archived.length === 0) return null;
    return (
      <div>
        <button
          type={'button'}
          aria-expanded={isArchivedOpen}
          aria-controls={isArchivedOpen ? ARCHIVED_LIST_ID : undefined}
          onClick={() => setIsArchivedOpen((open) => !open)}
          className={
            'flex items-center gap-2 text-gray-500 hover:text-gray-700 focus:outline-none'
          }
        >
          {isArchivedOpen ? (
            <ChevronDown wh={16} color={'#6B7280'} />
          ) : (
            <ChevronRight wh={16} color={'#6B7280'} />
          )}
          {fillText(t('archived.heading'), { count: archived.length })}
        </button>
        {isArchivedOpen ? (
          <ul id={ARCHIVED_LIST_ID}>
            {archived.map((perimeter) => (
              <li
                key={perimeter.id}
                className={
                  'py-2 border-b flex justify-between gap-2 items-center'
                }
              >
                <div className={'min-w-0'}>
                  <div
                    className={'truncate text-gray-600'}
                    title={perimeter.name}
                  >
                    {perimeter.name}
                  </div>
                  <div className={'text-gray-500'}>
                    {archivedDetails(perimeter)}
                  </div>
                </div>
                <Button
                  size={'SM'}
                  color={'SECONDARY'}
                  disabled={restoringId !== null}
                  aria-label={`${t('archived.restore')} ${perimeter.name}`}
                  onClick={() => handleRestore(perimeter)}
                >
                  {t('archived.restore')}
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  };

  const renderBody = () => {
    if (status === 'loading') {
      return (
        <div
          className={'flex flex-col gap-3 py-2 animate-pulse'}
          data-testid={'perimeters-loading'}
        >
          {[0, 1].map((row) => (
            <div key={row} className={'flex flex-col gap-2'}>
              <div className={'h-4 w-1/3 bg-gray-200 rounded'} />
              <div className={'h-4 w-1/2 bg-gray-200 rounded'} />
            </div>
          ))}
        </div>
      );
    }
    if (status === 'error') {
      return (
        <div className={'py-2 flex gap-2 items-center justify-between'}>
          <div className={'text-red-500'}>{t('list.loadError')}</div>
          <Button size={'SM'} color={'SECONDARY'} onClick={perimeters.retry}>
            {t('list.retry')}
          </Button>
        </div>
      );
    }
    return (
      <div className={'flex flex-col gap-3'}>
        {renderActive()}
        {renderArchived()}
      </div>
    );
  };

  return (
    <div className={'flex flex-col pt-2'} data-testid={'company-perimeters'}>
      <div className={'flex gap-2 justify-between items-start pb-2'}>
        <div className={'min-w-0'}>
          <div className={'flex gap-2 items-center'}>
            <div className={'font-semibold'}>{t('title')}</div>
            {status === 'ready' ? (
              <div
                data-testid={'perimeters-count'}
                className={
                  'my-auto bg-yellow-100 text-yellow-800 rounded px-1 flex items-center justify-center min-w-[20px]'
                }
              >
                {active.length}
              </div>
            ) : null}
          </div>
          <div className={'text-gray-500'}>{t('description')}</div>
        </div>
        <div className={'flex gap-2 shrink-0'}>
          <Button
            color={canCreate ? 'SECONDARY' : 'DISABLED'}
            size={'SM'}
            disabled={!canCreate}
            onClick={handleCreate}
          >
            <div className={'flex gap-1 items-center'}>
              <Plus wh={20} color={canCreate ? '#374151' : '#9CA3AF'} />
              <span>{t('list.create')}</span>
            </div>
          </Button>
          <Button
            size={'ICON'}
            color={'SECONDARY'}
            aria-expanded={!isCollapsed}
            onClick={() => setIsCollapsed((collapsed) => !collapsed)}
          >
            {isCollapsed ? (
              <ChevronRight wh={20} color={'#374151'} />
            ) : (
              <ChevronDown wh={20} color={'#374151'} />
            )}
            <span className={'sr-only'}>
              {t(isCollapsed ? 'expand' : 'collapse')}
            </span>
          </Button>
        </div>
      </div>
      {isCollapsed ? null : renderBody()}
    </div>
  );
};

export default SlideOverCompanyPerimeters;
